const TAX_ID_LABEL_BY_COUNTRY: Record<string, string> = {
  AU: 'ABN',
  GB: 'VAT ID',
  ZA: 'VAT ID',
  CH: 'VAT ID',
  IN: 'GSTIN',
  CA: 'GST/HST Number',
  NZ: 'GST Number',
  // EU countries
  AT: 'VAT ID',
  BE: 'VAT ID',
  BG: 'VAT ID',
  HR: 'VAT ID',
  CY: 'VAT ID',
  CZ: 'VAT ID',
  DK: 'VAT ID',
  EE: 'VAT ID',
  FI: 'VAT ID',
  FR: 'VAT ID',
  DE: 'VAT ID',
  GR: 'VAT ID',
  HU: 'VAT ID',
  IE: 'VAT ID',
  IT: 'VAT ID',
  LV: 'VAT ID',
  LT: 'VAT ID',
  LU: 'VAT ID',
  MT: 'VAT ID',
  NL: 'VAT ID',
  PL: 'VAT ID',
  PT: 'VAT ID',
  RO: 'VAT ID',
  SK: 'VAT ID',
  SI: 'VAT ID',
  ES: 'VAT ID',
  SE: 'VAT ID',
}

export const useTaxIdentifierLabel = (currency: string | undefined, countryIso2: string | undefined) => {
  const iso2 = countryIso2?.toUpperCase?.()
  if (iso2 && iso2 in TAX_ID_LABEL_BY_COUNTRY) {
    return TAX_ID_LABEL_BY_COUNTRY[iso2]
  }
  return 'Tax Number'
}

type TaxIdentifierHint = {
  descriptionKey: string
  descriptionFallback: string
  example: string
}

type TranslateFn = (key: string, options?: Record<string, any>) => string

const EU_TAX_ID_HINT_DESCRIPTION = '2-letter country code followed by 2-12 alphanumeric characters'
const EU_TAX_ID_HINT_ISO2_CODES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
] as const

const TAX_ID_HINT_BY_ISO2: Record<string, { descriptionFallback: string; example: string }> = {
  US: {
    descriptionFallback: '9-digit Employer Identification Number (EIN), often formatted XX-XXXXXXX',
    example: '12-3456789',
  },
  GB: { descriptionFallback: 'GB followed by 9 digits', example: 'GB123456789' },
  NZ: { descriptionFallback: '9 digits', example: '123456789' },
  IN: { descriptionFallback: '15 alphanumeric characters', example: '22AAAAA0000A1Z5' },
  JP: { descriptionFallback: 'T followed by 13 digits, or 13 digits', example: 'T1234567890123' },
  PK: { descriptionFallback: '15 alphanumeric characters', example: '1234567890ABCDE' },
  CH: { descriptionFallback: 'CHE followed by 9 digits', example: 'CHE123456789' },
  ZA: { descriptionFallback: 'ZA followed by 9 digits', example: 'ZA123456789' },
}

export const getTaxIdentifierHint = (
  countryIso2: string | undefined
): TaxIdentifierHint | null => {
  const iso2 = countryIso2?.toUpperCase?.() ?? ''
  const hint = TAX_ID_HINT_BY_ISO2[iso2]
  if (hint) {
    return {
      descriptionKey: `resources.wallet.tax_identifier_hints.by_iso2_code.${iso2}.description`,
      descriptionFallback: hint.descriptionFallback,
      example: hint.example,
    }
  }

  if (EU_TAX_ID_HINT_ISO2_CODES.includes(iso2 as (typeof EU_TAX_ID_HINT_ISO2_CODES)[number])) {
    return {
      descriptionKey: `resources.wallet.tax_identifier_hints.by_iso2_code.${iso2}.description`,
      descriptionFallback: EU_TAX_ID_HINT_DESCRIPTION,
      example: `${iso2}123456789`,
    }
  }

  return null
}

export const formatTaxIdentifierHintText = (
  translate: TranslateFn,
  countryIso2: string | undefined
): string | null => {
  const hint = getTaxIdentifierHint(countryIso2)
  if (!hint) return null

  const translatedDescription = translate(hint.descriptionKey, { _: hint.descriptionFallback })

  return translate('resources.wallet.tax_identifier_hints.format', {
    _: `Format: ${translatedDescription} (e.g. ${hint.example})`,
    description: translatedDescription,
    example: hint.example,
  })
}
